// frontend/packages/business-console/src/pages/Quotes/QuoteDocumentView.jsx
//
// WYSIWYG document body for the Quote detail page. Mirrors the visual
// shape of the rendered PDF (work item 2.3 — pdf-render sidecar) so
// the operator sees the same layout the customer will receive. Not
// pixel-perfect to the actual PDF bytes — for the canonical render,
// `quote.pdf.blobUrl` is the source of truth and a "Download PDF"
// link surfaces it.
//
// This component is the same shape the editor (2.14) will use — WYSIWYG
// single-pane was the design call (see docs/design/quotes-invoices-console.md
// §3.1). For the read-only detail page, line items + totals are
// non-interactive; the editor will overlay edit affordances in 2.14.

import React from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDate(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function customerDisplayName(customer) {
  if (!customer) return 'Customer';
  if (customer.companyName) return customer.companyName;
  const fn = customer.firstName || '';
  const ln = customer.lastName || '';
  const joined = `${fn} ${ln}`.trim();
  return joined || 'Customer';
}

function customerAddressLines(customer) {
  if (!customer || !customer.address) return [];
  const a = customer.address;
  const lines = [];
  if (a.line1) lines.push(a.line1);
  if (a.line2) lines.push(a.line2);
  const cityLine = [a.city, a.state].filter(Boolean).join(', ');
  const csz = [cityLine, a.postalCode].filter(Boolean).join('  ');
  if (csz) lines.push(csz);
  return lines;
}

export default function QuoteDocumentView({ quote, customer, business }) {
  if (!quote) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        maxWidth: 720,
        width: '100%',
        // WYSIWYG cues: page-like rendering + clean serif body but
        // sans-serif headers (matches the pdf template's stack).
        fontFamily:
          "'Liberation Sans', 'Noto Sans', Arial, system-ui, sans-serif",
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 5 } }}>
        {/* ── Header: business identity (left) + Quote label (right) ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.01em',
              }}
            >
              {business?.name || ''}
            </Typography>
            {business?.address ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
              >
                {business.address}
              </Typography>
            ) : null}
            {business?.email ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {business.email}
              </Typography>
            ) : null}
            {business?.phone ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {business.phone}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: 'text.secondary',
                textTransform: 'uppercase',
              }}
            >
              Estimate
            </Typography>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'text.primary',
                mt: 0.5,
              }}
            >
              {quote.number}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
            >
              Issued {formatDate(quote.createdAt)}
            </Typography>
            {quote.validUntil ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                Valid until {formatDate(quote.validUntil)}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ── Bill-to block ─────────────────────────────────────── */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Bill to
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mt: 0.5,
              fontSize: 15,
            }}
          >
            {customerDisplayName(customer)}
          </Typography>
          {customer?.email ? (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              {customer.email}
            </Typography>
          ) : null}
          {customerAddressLines(customer).map((line, idx) => (
            <Typography
              key={idx}
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              {line}
            </Typography>
          ))}
        </Box>

        {quote.title ? (
          <Typography
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mt: 3,
              fontSize: 14,
            }}
          >
            {quote.title}
          </Typography>
        ) : null}

        <Divider sx={{ my: 3 }} />

        {/* ── Line items ────────────────────────────────────────── */}
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 32px 66px 72px',
                sm: '1fr 60px 100px 110px',
              },
              gap: 1,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <HeaderCell>Description</HeaderCell>
            <HeaderCell align="right">Qty</HeaderCell>
            <HeaderCell align="right">Unit price</HeaderCell>
            <HeaderCell align="right">Line total</HeaderCell>
          </Box>
          {(quote.lineItems || []).map((li) => (
            <Box
              key={li.id || li.position}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr 32px 66px 72px',
                  sm: '1fr 60px 100px 110px',
                },
                gap: 1,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                alignItems: 'baseline',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.primary',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {li.description || '—'}
                </Typography>
              </Box>
              <Cell align="right">{li.quantity}</Cell>
              <Cell align="right">{fmtUsd(li.unitPrice)}</Cell>
              <Cell align="right" bold>
                {fmtUsd(li.lineTotal)}
              </Cell>
            </Box>
          ))}
        </Box>

        {/* ── Totals (right-aligned column) ─────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
          }}
        >
          <Stack
            spacing={0.75}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 0, sm: 240 },
              '& > *': {
                display: 'flex',
                justifyContent: 'space-between',
              },
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Subtotal
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  color: 'text.primary',
                }}
              >
                {fmtUsd(quote.subtotal)}
              </Typography>
            </Box>
            {quote.discountTotal > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Discount
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.primary',
                  }}
                >
                  −{fmtUsd(quote.discountTotal)}
                </Typography>
              </Box>
            ) : null}
            {quote.taxTotal > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Tax
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.primary',
                  }}
                >
                  {fmtUsd(quote.taxTotal)}
                </Typography>
              </Box>
            ) : null}
            <Divider sx={{ mt: 0.5, mb: 0.5 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Total
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtUsd(quote.total)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ── Notes + terms ─────────────────────────────────────── */}
        {quote.notes || quote.terms ? (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            {quote.notes ? (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Notes
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {quote.notes}
                </Typography>
              </Box>
            ) : null}
            {quote.terms ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Terms
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {quote.terms}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
}

function HeaderCell({ children, align }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        textAlign: align || 'left',
        fontSize: 10,
      }}
    >
      {children}
    </Typography>
  );
}

function Cell({ children, align, bold }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        color: 'text.primary',
        textAlign: align || 'left',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: bold ? 700 : 400,
      }}
    >
      {children}
    </Typography>
  );
}
